import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, Send, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";

export default function AdminAlerts() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/alerts/config");
    setCfg(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/admin/alerts/config", cfg);
      toast.success("Alert settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/alerts/test");
      const tg = data.results?.telegram;
      const em = data.results?.email;
      toast(`Telegram: ${tg?.ok ? "sent ✅" : (tg?.skipped || "failed")} · Email: ${em?.ok ? "sent ✅" : (em?.skipped || "failed")}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-zinc-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-alerts-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Notifications</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Alerts</h1>
        <p className="text-zinc-500 mt-1">
          Get notified when a reseller runs low on diamonds or when a platform session cookie expires.
        </p>
      </div>

      {/* Thresholds */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Low-balance threshold (diamonds)</Label>
            <Input type="number" min="0" value={cfg.low_balance_threshold}
              onChange={(e) => set("low_balance_threshold", e.target.value)} data-testid="threshold-input" />
            <p className="text-xs text-zinc-500 mt-1">Alert when a reseller&apos;s platform balance drops below this.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Cookie check interval (days)</Label>
            <Input type="number" min="1" value={cfg.cookie_check_days}
              onChange={(e) => set("cookie_check_days", e.target.value)} data-testid="cookie-days-input" />
            <p className="text-xs text-zinc-500 mt-1">How often to auto-check each platform session.</p>
          </div>
        </div>
      </div>

      {/* Telegram */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-sky-500" />
            <span className="font-medium text-zinc-900">Telegram</span>
          </div>
          <Switch checked={!!cfg.telegram_enabled} onCheckedChange={(v) => set("telegram_enabled", v)} data-testid="telegram-enabled-switch" />
        </div>
        <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3 leading-relaxed">
          <strong>How to set up:</strong> 1) On Telegram, search <code>@BotFather</code> → send <code>/newbot</code> → copy the <strong>Bot Token</strong>.
          2) Search <code>@userinfobot</code> → it replies with your <strong>Chat ID</strong>. 3) Start a chat with your new bot (send it any message) so it can message you. Paste both below.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Bot Token</Label>
            <Input value={cfg.telegram_token} onChange={(e) => set("telegram_token", e.target.value)} placeholder="123456:ABC-DEF…" data-testid="telegram-token-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Chat ID</Label>
            <Input value={cfg.telegram_chat_id} onChange={(e) => set("telegram_chat_id", e.target.value)} placeholder="987654321" data-testid="telegram-chat-input" />
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-violet-500" />
            <span className="font-medium text-zinc-900">Email (Resend)</span>
          </div>
          <Switch checked={!!cfg.email_enabled} onCheckedChange={(v) => set("email_enabled", v)} data-testid="email-enabled-switch" />
        </div>
        <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3 leading-relaxed">
          <strong>How to set up:</strong> Sign up free at <code>resend.com</code> → API Keys → create a key (starts with <code>re_</code>). Paste it below with the email(s) that should receive alerts. (Until you verify your own domain in Resend, keep the sender as <code>onboarding@resend.dev</code> and alerts can only be sent to your Resend account email.)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Resend API Key</Label>
            <Input value={cfg.resend_key} onChange={(e) => set("resend_key", e.target.value)} placeholder="re_…" data-testid="resend-key-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Sender email</Label>
            <Input value={cfg.sender_email} onChange={(e) => set("sender_email", e.target.value)} placeholder="onboarding@resend.dev" data-testid="sender-email-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Recipient email(s)</Label>
          <Input value={cfg.recipient_emails} onChange={(e) => set("recipient_emails", e.target.value)} placeholder="you@example.com, partner@example.com" data-testid="recipient-emails-input" />
          <p className="text-xs text-zinc-500 mt-1">Comma-separated.</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={loading} className="bg-zinc-900 hover:bg-zinc-800" data-testid="save-alerts-button">
          <Bell className="w-4 h-4 mr-1" /> {loading ? "Saving…" : "Save settings"}
        </Button>
        <Button onClick={sendTest} disabled={testing} variant="outline" data-testid="test-alerts-button">
          <Send className="w-4 h-4 mr-1" /> {testing ? "Sending…" : "Send test alert"}
        </Button>
        {cfg.last_cookie_check && (
          <span className="text-xs text-zinc-400 font-mono ml-auto">Last cookie check: {new Date(cfg.last_cookie_check).toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}
